import React, { useState, useEffect } from 'react';
import './App.css'; // 如果你有相应的 CSS 文件，可以引用
import ImageTracer from 'imagetracerjs';

const FillEdges = ({
  imageWidth = 800,
  imageHeight = 600,
  fillColor = "#ffffff",
  strokeColor = "#000000",
  strokeWidth = 2,
  minDotSize = 0,
  maxDotSize = 8,
  dotRandomness = 5,
  backgroundThreshold = 245,
  backgroundColor = "#ffffff",
  aColor = "#0000ff",
  aAngle = 45,
  aThickness = 2,
  aSpacing = 20,
  bColor = "#00ff00",
  bAngle = 69,
  bThickness = 2,
  bSpacing = 20
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [svgResult, setSvgResult] = useState(null);

  // 控件参数更新后，重新生成 SVG
  useEffect(() => {
    if (imageSrc) {
      processImage();
    }
  }, [
    imageWidth,
    imageHeight,
    fillColor,
    strokeColor,
    strokeWidth,
    minDotSize,
    maxDotSize,
    dotRandomness,
    backgroundThreshold,
    backgroundColor,
    aColor,
    aAngle,
    aThickness,
    aSpacing,
    bColor,
    bAngle,
    bThickness,
    bSpacing,
    imageSrc
  ]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageSrc(event.target.result);
        setSvgResult(null); // 重置SVG结果
      };
      reader.readAsDataURL(file);
    }
  };

  const preprocessImage = (ctx, width, height) => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const brightness = (r + g + b) / 3;
      if (brightness > backgroundThreshold) {
        data[i + 3] = 0;
      }
    }
    ctx.putImageData(imageData, 0, 0);
  };

  const generateRandomDots = (pathData) => {
    let dots = [];
    const pathCommands = pathData.match(/[MLC][^MLC]*/g);
    if (!pathCommands) return "";

    pathCommands.forEach(cmd => {
      const coords = cmd.slice(1).trim().split(/[\s,]+/).map(Number);
      for (let i = 0; i < coords.length; i += 2) {
        if (Math.random() < 0.2) {
          const x = coords[i];
          const y = coords[i + 1];
          const randomSize = Math.max(minDotSize, Math.min(maxDotSize, minDotSize + Math.random() * dotRandomness));
          dots.push(`<circle cx="${x}" cy="${y}" r="${randomSize}" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}"/>`);
        }
      }
    });

    return dots.join("");
  };

  const generateGridPattern = (id, angle, color, thickness, spacing) => {
    return `
      <pattern id="${id}" patternUnits="userSpaceOnUse" width="${spacing}" height="${spacing}" patternTransform="rotate(${angle})">
        <line x1="0" y1="0" x2="0" y2="${spacing}" stroke="${color}" stroke-width="${thickness}" />
      </pattern>
    `;
  };

  const processImage = () => {
    if (!imageSrc) return;

    const img = new Image();
    img.src = imageSrc;
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = imageWidth;
      canvas.height = imageHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, imageWidth, imageHeight);

      preprocessImage(ctx, imageWidth, imageHeight);
      const imageData = ctx.getImageData(0, 0, imageWidth, imageHeight);

      const options = {
        pathomit: 8,
        rightangleenhance: true,
        colorquantcycles: 3,
        colordepth: 6,
        numberofcolors: 2
      };

      try {
        let svgString = ImageTracer.imagedataToSVG(imageData, options);

        // **提取所有 `<path>` 作为 `clipPath`**
        const pathMatches = svgString.match(/<path[^>]+d="([^"]+)"/g);
        if (!pathMatches) {
          console.error("No paths found for clipPath.");
          return;
        }

        const combinedPaths = pathMatches.join(" ");

        // **创建 `clipPath`**
        const clipPathId = "clipGrid";
        const clipPath = `
          <defs>
            <clipPath id="${clipPathId}">
              ${combinedPaths}
            </clipPath>
          </defs>
        `;

        // **生成 `pattern` 填充**
        const gridPatternA = generateGridPattern("gridPatternA", aAngle, aColor, aThickness, aSpacing);
        const gridPatternB = generateGridPattern("gridPatternB", bAngle, bColor, bThickness, bSpacing);

        // **生成随机圆点**
        const dotsSvg = `<g id="dotsLayer">${generateRandomDots(combinedPaths)}</g>`;

        // **修正 `SVG` 结构**
        svgString = svgString.replace("<svg", `<svg style="background-color:${backgroundColor};"`);

        // **确保 `SVG` 轮廓保留**
        svgString = svgString.replace(
          /<path /g,
          `<path fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" `
        );

        // **添加 `clipPath`、随机圆点、网格填充**
        svgString = svgString.replace(
          "</svg>",
          `${clipPath}${gridPatternA}${gridPatternB}
          <g clip-path="url(#${clipPathId})">
            <rect width="${imageWidth}" height="${imageHeight}" fill="url(#gridPatternA)"/>
            <rect width="${imageWidth}" height="${imageHeight}" fill="url(#gridPatternB)"/>
          </g>
          ${dotsSvg}
          </svg>`
        );

        setSvgResult(svgString);
      } catch (error) {
        console.error("Error processing image:", error);
      }
    };
  };

  return (
    <div className="fill-edges-container">
      <div className="canvas-panel-header">
        {/* 🎯 隐藏原始文件选择按钮 */}
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          id="fileInput"
          style={{ display: 'none' }}
        />

        {/* 🎯 创建自定义按钮触发文件选择 */}
        <label htmlFor="fileInput" className="custom-upload-btn">
          {imageSrc ? 'changeImage' : 'choseImage'}
        </label>

        {/* 🎯 处理图像按钮 */}
        {imageSrc && <button className="action-btn" onClick={processImage}>processImage</button>}
      </div>
      
      {/* 🎯 SVG 显示 */}
      <div className="svg-container" dangerouslySetInnerHTML={{ __html: svgResult }} />
    </div>
  );
};

export default FillEdges;
